"use client";

import type { FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";

import { isEditing, setClientUserAttributes } from "@builder.io/sdk-react";

import { Loader } from "@jti/core-components/components/Loader";
import { useUser } from "@jti/ui/hooks/useUser";

import { getBuilderChildrenCount } from "@/libs/builder/utils/getBuilderChildrenCount";

import { Placeholder } from "../Placeholder/Placeholder";

import { CustomerType, useCustomerType } from "./hooks/useCustomerType";

import styles from "./PersonalizationContainer.module.scss";

type PersonalizationContainerProps = PropsWithChildren<{
  renderFullViewportLoader?: boolean;
}>;

const getDeviceType = () => {
  if (typeof window === "undefined") return "desktop";

  if (window.matchMedia("(max-width: 767px)").matches) return "mobile";
  if (window.matchMedia("(min-width: 768px) and (max-width: 1279px)").matches) return "tablet";
  return "desktop";
};

enum DeviceStatusValue {
  new = "new",
  returning = "returning",
}

const STORAGE_KEY = "deviceVisitedState";
const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;

const readCookie = (name: string): string => {
  const entry = document.cookie.split("; ").find((cookie) => cookie.startsWith(`${name}=`));
  const value = entry?.split("=")[1] ?? "";

  return decodeURIComponent(value);
};

type TrafficAttributes = {
  trafficSource: string;
};

const getTrafficAttributes = (): TrafficAttributes => ({
  trafficSource: readCookie("trafficSource") || "direct",
});

let cachedVisitorType: DeviceStatusValue | null = null;

type StoredVisitorState = {
  visitorType: DeviceStatusValue;
  lastVisitAt: number;
};

const getStoredVisitorState = (): StoredVisitorState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) return null;

    const parsedValue = JSON.parse(stored) as Partial<StoredVisitorState>;

    return { visitorType: parsedValue.visitorType!, lastVisitAt: parsedValue.lastVisitAt! };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const saveVisitorState = (visitorType: DeviceStatusValue): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ visitorType, lastVisitAt: Date.now() }));
};

const isPageReload = (): boolean => {
  const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;

  return navigationEntry?.type === "reload";
};

const resolveVisitorType = (): DeviceStatusValue => {
  if (cachedVisitorType) {
    return cachedVisitorType;
  }

  const storedState = getStoredVisitorState();
  const isExpired = !storedState || Date.now() - storedState.lastVisitAt > THIRTY_DAYS;

  if (isExpired) {
    cachedVisitorType = DeviceStatusValue.new;
    saveVisitorState(cachedVisitorType);
    return cachedVisitorType;
  }

  if (isPageReload()) {
    cachedVisitorType = storedState.visitorType;
    saveVisitorState(cachedVisitorType);
    return cachedVisitorType;
  }

  cachedVisitorType = DeviceStatusValue.returning;
  saveVisitorState(cachedVisitorType);
  return cachedVisitorType;
};

type VisitorAttributes = {
  visitorType: DeviceStatusValue;
  device: "mobile" | "tablet" | "desktop";
  province: string;
  customerType: CustomerType;
} & TrafficAttributes;

const getVisitorAttributes = (): VisitorAttributes => ({
  visitorType: resolveVisitorType(),
  device: getDeviceType(),
  province: readCookie("province"),
  customerType: CustomerType.unknown,
  ...getTrafficAttributes(),
});

export const PersonalizationContainer: FC<PersonalizationContainerProps> = ({
  children,
  renderFullViewportLoader = false,
}) => {
  const [visitorAttributes, setVisitorAttributes] = useState<VisitorAttributes | null>(null);
  const { isUserLoggedIn, isCheckingUser } = useUser();
  const customerType = useCustomerType(isUserLoggedIn);

  const [builderReady, setBuilderReady] = useState(false);

  useEffect(() => {
    setVisitorAttributes(getVisitorAttributes());
  }, []);

  useEffect(() => {
    if (!visitorAttributes || isCheckingUser) return;

    setClientUserAttributes({
      ...visitorAttributes,
      userLoginStatus: isUserLoggedIn ? "loggedIn" : "loggedOut",
      customerType,
    });

    setBuilderReady(true);
  }, [visitorAttributes, isCheckingUser, isUserLoggedIn, customerType]);

  if (!visitorAttributes || isCheckingUser || !builderReady) {
    return renderFullViewportLoader ? (
      <div className={styles.reservedSpace} aria-hidden="true">
        <Loader />
      </div>
    ) : null;
  }

  if (isEditing() && getBuilderChildrenCount(children) === 0) {
    return <Placeholder>Add variant container here</Placeholder>;
  }

  return <div>{children}</div>;
};
